import { useRuntimeVariables } from 'app-kit'
import { AuthedAxiosRequests, useGeckoAuth } from 'features/auth'
import { useMemo } from 'react'
import { ChartTimeUnit } from 'types'

export interface TimeSeriesDataPoint {
  entry_id: string
  measurement: string
  timestamp: string
  value: number | null
}

export interface TimeSeriesResponse {
  [key: string]: TimeSeriesDataPoint[]
}

export enum AggregationOp {
  MEAN = 'avg',
  SUM = 'sum',
  MIN = 'min',
  MAX = 'max',
  COUNT = 'count',
}

export interface TimeSeriesRequest {
  entry_ids: string[]
  start_datetime: string
  end_datetime: string
  interval?: number
  interval_unit?: ChartTimeUnit
  aggregation_op?: AggregationOp
  interpolate?: boolean
}

export interface StreamingOptions {
  onProgress?: (progress: {
    bytesProcessed: number
    totalBytes?: number
    itemsProcessed: number
  }) => void
  onChunk?: (data: TimeSeriesResponse) => void
  chunkSize?: number
  maxBufferSize?: number
  signal?: AbortSignal
}

// Temporary: Use dev time series endpoint until production endpoint is ready

export class TimeSeriesService {
  private axios: AuthedAxiosRequests
  private readonly TIME_SERIES_BASE_URL: string

  constructor(axios: AuthedAxiosRequests, timeSeriesBaseUrl: string) {
    this.axios = axios
    this.TIME_SERIES_BASE_URL = timeSeriesBaseUrl
  }

  static getInterval(
    seconds: number,
    max_data_points: number = 10000
  ): {
    interval: number
    interval_unit: ChartTimeUnit
  } {
    // Calculate the required interval in seconds to achieve max_data_points
    const requiredIntervalSeconds = seconds / max_data_points

    if (requiredIntervalSeconds <= 1) {
      return { interval: 1, interval_unit: ChartTimeUnit.SECONDS }
    } else if (requiredIntervalSeconds < 60) {
      return {
        interval: Math.max(1, Math.round(requiredIntervalSeconds)),
        interval_unit: ChartTimeUnit.SECONDS,
      }
    } else if (requiredIntervalSeconds < 3600) {
      const intervalMinutes = Math.max(
        1,
        Math.round(requiredIntervalSeconds / 60)
      )
      return { interval: intervalMinutes, interval_unit: ChartTimeUnit.MINUTES }
    } else if (requiredIntervalSeconds < 86400) {
      const intervalHours = Math.max(
        1,
        Math.round(requiredIntervalSeconds / 3600)
      )
      return { interval: intervalHours, interval_unit: ChartTimeUnit.HOURS }
    } else {
      // Convert to days for larger intervals
      const intervalDays = Math.max(
        1,
        Math.round(requiredIntervalSeconds / 86400)
      )
      return { interval: intervalDays, interval_unit: ChartTimeUnit.DAYS }
    }
  }

  /**
   * Fetches time series data with streaming support for large datasets
   * Processes data incrementally to avoid memory issues with large responses
   */
  async fetchTimeSeriesDataStream(
    request: TimeSeriesRequest,
    options?: StreamingOptions
  ): Promise<TimeSeriesResponse> {
    const requestBody = {
      ...request,
      interval: request.interval || 1,
      interval_unit: request.interval_unit || ChartTimeUnit.SECONDS,
      aggregation_op: request.aggregation_op || AggregationOp.MEAN,
      gap_fill: request.interpolate ? 'interpolate' : 'include_nulls',
    }

    const {
      onProgress,
      onChunk,
      chunkSize = 65536, // 64KB default chunk size
      maxBufferSize = 10 * 1024 * 1024, // 10MB max buffer
      signal,
    } = options || {}

    try {
      const response = await this.axios.post(
        `${this.TIME_SERIES_BASE_URL}/api/v2/time-series/read/aggregate`,
        requestBody,
        {
          adapter: 'fetch',
          responseType: 'stream',
          signal,
          onDownloadProgress: (progressEvent) => {
            if (onProgress && progressEvent.loaded) {
              onProgress({
                bytesProcessed: progressEvent.loaded,
                totalBytes: progressEvent.total,
                itemsProcessed: 0, // Will be updated during parsing
              })
            }
          },
        }
      )

      if (!response.data) {
        throw new Error('Empty response from time series API')
      }

      return await this.processStreamResponse(response.data, {
        onProgress,
        onChunk,
        chunkSize,
        maxBufferSize,
        signal,
      })
    } catch (error) {
      console.error('Error fetching streaming time series data:', error)
      throw new Error(
        `Time series streaming API failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  /**
   * Process streaming response data incrementally
   */
  private async processStreamResponse(
    stream: ReadableStream | string,
    options: StreamingOptions
  ): Promise<TimeSeriesResponse> {
    const { onProgress, onChunk, maxBufferSize = 10 * 1024 * 1024 } = options

    if (typeof stream === 'string') {
      return this.parseTimeSeriesData(stream)
    }

    const reader =
      stream instanceof ReadableStream
        ? stream.getReader()
        : this.createReaderFromData(stream)

    const decoder = new TextDecoder('utf-8')
    let buffer = ''
    let bytesProcessed = 0
    let itemsProcessed = 0
    const result: TimeSeriesResponse = {}

    try {
      while (true) {
        const { done, value } = await reader.read()

        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        buffer += chunk
        bytesProcessed += value.byteLength

        if (buffer.length > maxBufferSize) {
          throw new Error(
            `Buffer size exceeded maximum of ${maxBufferSize} bytes`
          )
        }

        // Process complete lines (NDJSON format)
        const lines = buffer.split('\n')
        buffer = lines.pop() || '' // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.trim()) continue

          try {
            const parsed = JSON.parse(line) as Record<
              string,
              TimeSeriesDataPoint[]
            >

            // Merge parsed data into result
            Object.entries(parsed).forEach(([key, points]) => {
              if (!result[key]) {
                result[key] = []
              }
              result[key].push(...points)
              itemsProcessed += points.length
            })

            // Call chunk callback if provided
            if (onChunk) {
              onChunk(parsed)
            }

            // Update progress
            if (onProgress) {
              onProgress({
                bytesProcessed,
                itemsProcessed,
                totalBytes: undefined,
              })
            }
          } catch (parseError) {
            // Log but continue processing other lines
            console.warn('Failed to parse line:', line, parseError)
          }
        }
      }

      // Process any remaining data in buffer
      if (buffer.trim()) {
        try {
          const parsed = JSON.parse(buffer) as Record<
            string,
            TimeSeriesDataPoint[]
          >
          Object.entries(parsed).forEach(([key, points]) => {
            if (!result[key]) {
              result[key] = []
            }
            result[key].push(...points)
            itemsProcessed += points.length
          })
        } catch (parseError) {
          console.warn('Failed to parse final buffer:', buffer, parseError)
        }
      }
      return result
    } finally {
      reader.releaseLock?.()
    }
  }

  /**
   * Create a reader from various data types
   */
  private createReaderFromData(data: unknown): ReadableStreamDefaultReader {
    let stream: ReadableStream

    if (data instanceof ReadableStream) {
      stream = data
    } else if (
      typeof data === 'object' &&
      data !== null &&
      'getReader' in data
    ) {
      stream = data as ReadableStream
    } else {
      const encoder = new TextEncoder()
      const uint8Array = encoder.encode(String(data))
      stream = new ReadableStream({
        start(controller) {
          controller.enqueue(uint8Array)
          controller.close()
        },
      })
    }

    return stream.getReader()
  }

  /**
   * Parse time series data from string (fallback for non-streaming)
   */
  private parseTimeSeriesData(data: string): TimeSeriesResponse {
    // Try standard JSON first
    try {
      return JSON.parse(data) as TimeSeriesResponse
    } catch {
      // Try NDJSON format
      return this.parseNDJSON(data)
    }
  }

  /**
   * Parse NDJSON (newline-delimited JSON) format
   */
  private parseNDJSON(data: string): TimeSeriesResponse {
    const lines = data.trim().split('\n')
    const result: TimeSeriesResponse = {}

    for (const line of lines) {
      if (!line.trim()) continue

      try {
        const parsed = JSON.parse(line) as Record<string, TimeSeriesDataPoint[]>
        Object.entries(parsed).forEach(([key, points]) => {
          if (!result[key]) {
            result[key] = []
          }
          result[key].push(...points)
        })
      } catch (error) {
        console.warn('Failed to parse NDJSON line:', line, error)
      }
    }

    if (Object.keys(result).length === 0) {
      throw new Error('No valid data parsed from NDJSON response')
    }

    return result
  }

  /**
   * Legacy method - now uses streaming internally but maintains same interface
   */
  async fetchTimeSeriesData(
    request: TimeSeriesRequest,
    options?: { signal?: AbortSignal }
  ): Promise<TimeSeriesResponse> {
    // Use streaming method internally for better performance
    return this.fetchTimeSeriesDataStream(request, { signal: options?.signal })
  }

  /**
   * Fetch latest sensor values for the given sensors
   * @param sensorIds Array of sensor IDs to fetch values for
   * @returns Object mapping sensor IDs to their latest values
   */
  async fetchLatestSensorValues(
    sensorIds: string[]
  ): Promise<Record<string, TimeSeriesDataPoint | null>> {
    if (!sensorIds || sensorIds.length === 0) {
      return {}
    }
    const requestBody = {
      entry_ids: sensorIds,
      count: 3,
    }

    try {
      const response = await this.axios.post(
        `${this.TIME_SERIES_BASE_URL}/api/v2/time-series/read/latest`,
        requestBody
      )
      if (!response.data) {
        throw new Error('Empty response from time series API')
      }
      // go from array to object mapping sensorId to data point or null
      const result: Record<string, TimeSeriesDataPoint | null> = {}
      const data = response.data as Record<string, TimeSeriesDataPoint[] | null>

      sensorIds.forEach((id) => {
        if (data[id] && data[id].length > 0) {
          // Find the latest data point by timestamp
          result[id] = data[id].at(-1)!
        } else {
          result[id] = null
        }
      })
      return result
    } catch (error) {
      console.error('Error fetching streaming time series data:', error)
      throw new Error(
        `Time series streaming API failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }
}

export const useTimeSeriesService = () => {
  const { authorizedAxios } = useGeckoAuth()
  const { TIME_SERIES_BASE_URL } = useRuntimeVariables()
  return useMemo(() => {
    const service = new TimeSeriesService(authorizedAxios, TIME_SERIES_BASE_URL)
    return {
      fetchTimeSeriesData: service.fetchTimeSeriesData.bind(service),
      fetchTimeSeriesDataStream:
        service.fetchTimeSeriesDataStream.bind(service),
      fetchLatestSensorValues: service.fetchLatestSensorValues.bind(service),
    }
  }, [TIME_SERIES_BASE_URL, authorizedAxios])
}
